import React, { memo } from 'react';
import classNames from 'classnames';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { getLocaleState } from '@home/utils/state';
import BasicImage from '@home/components/BasicImage';
import iconAIAnalysis from '@/pages/aigc/assets/svg/icon-ai-analysis.svg';
import './index.scss';

interface IAIAnalysisButtonProps {
  /** 报表标题，与按钮并排展示 */
  dashboardName?: React.ReactNode;
  /** 游戏 code，发送给父框架 + 打点 uid */
  gameCode?: string;
  /** 报表 ID，发送给父框架透传给 Copilot 用于「Current page」分组识别 */
  dashboardId?: string;
  /** 游戏名称，打点用 */
  gameName?: string;
  /** 打点 pageId */
  pageId?: string;
  /** 来源标识，默认 custom_dashboard；可由调用方覆盖 */
  form?: string;
  /** 外层容器额外类名 */
  className?: string;
}

/**
 * 报表标题 + AI 分析入口按钮（横向并排）
 * 仅在 datalab/customDashboard（system === 'dashboard'）场景下使用
 *
 * 注意：本组件渲染在 datalab iframe 内部。
 * 点击时通过 postMessage 通知父框架（DatalabIframe 调用方）打开 Copilot 抽屉，
 * 复用现有的 clickExplore 通信通道，用 payload.subType='aiAnalysis' 区分消息类型。
 *
 * 无 AI 权限（aigc / aigc_pc）的用户不渲染按钮，仅渲染原标题。
 */
const AIAnalysisButton: React.FC<IAIAnalysisButtonProps> = memo(({
  dashboardName,
  gameCode,
  dashboardId,
  gameName,
  pageId,
  form = 'custom_dashboard',
  className,
}: IAIAnalysisButtonProps) => {
  const handleClick = (): void => {
    // 1. 打点（在 iframe 内发起）
    buriedBtnPoint({
      buttonId: '208501902',
      buttonName: 'AIAnalysis',
      typeId: 'dashboard_pc_v2',
      pageId: pageId || '',
      uidType: 'game_code',
      uid: gameCode,
      gameName: gameName || '',
      extInfo: JSON.stringify({ form, gameCode, dashboardId }),
    });

    const datalabMessageBus = getLocaleState<{
      notifyExploreButtonClick: (payload: any) => void;
    }>('datalabMessageBus');
    datalabMessageBus?.notifyExploreButtonClick({
      subType: 'aiAnalysis',
      gameCode,
      dashboardId,
      form,
    });
  };

  return (
    <div className={classNames('dbh-ai-analysis-title', className)}>
      {dashboardName}
      <div className="dbh-ai-analysis-btn" onClick={handleClick}>
        <BasicImage className="dbh-ai-analysis-btn__icon" src={iconAIAnalysis} />
        <span className="dbh-ai-analysis-btn__text">AI Analysis</span>
      </div>
    </div>
  );
});

AIAnalysisButton.displayName = 'AIAnalysisButton';

export default AIAnalysisButton;
